/**
 * `EditorCommandName` (에디터 중립 명령) → NgwEditor `executeCommand` 명령 매핑 (03 §2.12, PR-N3).
 *
 * pr-n0-inventory.md §3 의 매핑표를 1:1 이전 (27 명령 — alignJustify / codeView 포함).
 * NgwEditor 0.2.10 의 `EditorHandle.executeCommand` 가 받는 명령 이름과 일치
 * (`core/src/features/<feature>/index.ts` 의 `commands` 객체에서 추출).
 *
 * ### 특수 처리 (단순 executeCommand 매핑이 아닌 명령) — `null` 마커
 *
 * 다음 명령은 어댑터의 `NgwEditorCommand.run()` 안에서 분기 처리한다 — 본 매핑표 외 로직 필요:
 *
 * - `link`          — `adapter.state.requestLinkInfo()` 위임 (PR-N6 wiring 후 실 동작)
 * - `alignLeft` / `alignCenter` / `alignRight` / `alignJustify`
 *                   — NgwEditor 는 단일 `setTextAlign(align)` + literal union arg (D-N5).
 *                     4 명령을 어댑터에서 `setTextAlign` + 적절한 align 값으로 매핑.
 * - `removeFormat`  — clear-formatting feature 의 `commands` 가 empty (D-N4). 어댑터 자체
 *                     ProseMirror plugin (`extensions/removeFormatExtension.ts`) 으로 `removeFormat`
 *                     extension command 등록. 본 매핑표는 `null` 로 두고 NgwEditorCommand 가
 *                     extension 의 executeCommand 경로로 분기.
 * - `codeView`      — NgwEditor 0.2.10 미지원. 어댑터 NoOp + 명시 로그 1회 (D-N4).
 * - `directionLTR` / `directionRTL`
 *                   — NgwEditor 미노출 (D-N4). PR-N2 의 `NgwEditorDirection.setRTL()` 으로 위임
 *                     (DOM `dir` attribute 토글). NgwEditorCommand 가 sub-adapter 호출로 분기.
 *
 * 그 외 단순 매핑 (현재 17개): 매핑 결과를 `handle.executeCommand(mapped, value)` 그대로 호출.
 *
 * ### 명령 이름 차이 (TinyMCE ↔ NgwEditor)
 *
 * - `strikethrough` → `toggleStrikethrough` (**NOT** `toggleStrike`, pr-n0-inventory §3 주의)
 * - `unlink`        → `removeLink` (이름 다름)
 * - `outdent`       → `decreaseIndent`, `indent` → `increaseIndent`
 * - `unorderedList` → `toggleBulletList` (NOT `toggleUnorderedList`)
 */

import type { EditorCommandName } from '../IEditorCommand'

export const NGW_COMMAND_MAP: Record<EditorCommandName, string | null> = {
  // 인라인 포맷
  bold: 'toggleBold',
  italic: 'toggleItalic',
  underline: 'toggleUnderline',
  strikethrough: 'toggleStrikethrough',
  // 색상
  foregroundColor: 'setFontColor',
  backgroundColor: 'setFontBgColor',
  // 폰트 — fontFamily 는 어댑터에서 사후 조회 (getCurrentFontFamily) 후 string 반환
  fontFamily: 'setFontFamily',
  fontSize: 'setFontSize',
  // Undo/Redo
  undo: 'undo',
  redo: 'redo',
  // 정렬 — NgwEditor 단일 명령 + literal union 인자 (D-N5)
  alignLeft: null, // 특수 — setTextAlign('left')
  alignCenter: null, // 특수 — setTextAlign('center')
  alignRight: null, // 특수 — setTextAlign('right')
  alignJustify: null, // 특수 — setTextAlign('justify')
  // 리스트
  orderedList: 'toggleOrderedList',
  unorderedList: 'toggleBulletList',
  // 들여쓰기
  outdent: 'decreaseIndent',
  indent: 'increaseIndent',
  // 링크
  link: null, // 특수 — adapter.state.requestLinkInfo() 위임
  unlink: 'removeLink',
  // 블럭/구분선
  blockquote: 'toggleBlockquote',
  // horizontalRule 은 매핑 가능하나 어댑터에서 variant 매핑 후속 호출 분기 필요
  horizontalRule: 'insertHorizontalRule',
  // 표
  insertTable: 'insertTable',
  // 방향 — NgwEditor 미노출. PR-N2 NgwEditorDirection.setRTL 위임 (사용자 결정 (a))
  directionLTR: null, // 특수 — adapter.direction.setRTL(false)
  directionRTL: null, // 특수 — adapter.direction.setRTL(true)
  // 포맷 초기화 — clear-formatting feature 의 commands 가 empty (D-N4). 어댑터 자체 PM extension
  // (extensions/removeFormatExtension.ts) 이 'removeFormat' 명령을 등록 — 일반 executeCommand 경로.
  removeFormat: 'removeFormat',
  // 소스/코드 보기 — NgwEditor 0.2.10 미지원. 어댑터 NoOp + 로그 (D-N4)
  codeView: null,
}
